import { Lang } from './i18n/locales';

export enum AppRoutes {
  HOME = '/',
  ABOUT = '/about',
  PRODUCTS = '/products',
  SHOP = '/shop', // Added Shop Route
  JOURNAL = '/journal',
  FAQ = '/faq',
  PET_SERVICES = '/pet-services',
  CONTACT = '/contact',
  SHIPPING = '/shipping',
  PRIVACY = '/privacy',
  TERMS = '/terms',
  STYLE_GUIDE = '/style-guide',
  LOGIN = '/login',
  ACCOUNT = '/account',
  PET_PROFILE = '/pet',

  // Cart & Checkout
  CART = '/cart',
  CHECKOUT = '/checkout',
  ORDER_CONFIRMATION = '/order-confirmation',
  ORDER_HISTORY = '/order-history',
  TRACK_ORDER = '/track-order',

  ADMIN = '/admin',
  ADMIN_JOURNAL = '/admin/journal',
  ADMIN_JOURNAL_NEW = '/admin/journal/new',
  ADMIN_JOURNAL_EDIT = '/admin/journal/edit',
  ADMIN_ORDERS = '/admin/orders',
  ADMIN_PRODUCTS = '/admin/products',
  ADMIN_PRODUCTS_NEW = '/admin/products/new',
  ADMIN_PRODUCTS_EDIT = '/admin/products/edit',
  ADMIN_INVENTORY = '/admin/inventory',
  ADMIN_REVIEWS = '/admin/reviews',
  ADMIN_VISIBILITY = '/admin/visibility',
  ADMIN_CHATS = '/admin/chats',
  ADMIN_PROMPTS = '/admin/prompts',
  ADMIN_NEWSLETTER = '/admin/newsletter',
}

export interface NavLinkItem {
  label: string;
  path: AppRoutes;
}

export const getLangPath = (path: string, lang: Lang) => {
  if (path.startsWith('http')) return path; // External
  if (path.startsWith('/admin')) return path; // Admin routes no prefix
  // Ensure path starts with slash and remove it for joining
  const cleanPath = path.startsWith('/') ? path.substring(1) : path;
  // If cleanPath is empty (Home), just return /en
  if (!cleanPath) return `/${lang}`;
  return `/${lang}/${cleanPath}`;
};

export const productDetailPath = (slug: string) => `${AppRoutes.PRODUCTS}/${slug}`;
export const journalPostPath = (slug: string) => `${AppRoutes.JOURNAL}/${slug}`;

// Helper for generating preview URLs (Clean URL)
export const journalPreviewPath = (slug: string, lang: 'en' | 'zh' = 'en') =>
  `/en/${AppRoutes.JOURNAL.substring(1)}/${slug}?preview=1&lang=${lang}`;

// --- Order Types ---

export interface CreateOrderPayload {
  customer: {
    name: string;
    phone: string;
    email?: string;
  };
  shippingAddress: {
    line1: string;
    city: string;
    country: string;
  };
  items: {
    slug: string;
    qty: number;
    title: { en: string; zh: string };
    unitPriceCents: number;
  }[];
  currency: string;
}

export interface OrderResponse {
  success: boolean;
  orderId: string;
  orderNumber: string;
  status: string;
  orderAccessToken?: string; // Only returned for guest orders
}

// --- Admin Order Types (Kept for Admin Portal) ---

export type OrderStatus =
  | 'pending_payment'
  | 'paid'
  | 'fulfilled'
  | 'cancelled'
  | 'refunded'
  | 'expired';

export interface AdminOrderItem {
  slug: string;
  qty: number;
}

export interface OrderAdminListItem {
  id: string;
  orderNumber: string;
  createdAt: string;
  status: OrderStatus;
  customerName: string;
  phone: string;
  email?: string | null;
  userId?: string | null;
  items: AdminOrderItem[];
  totalAmount?: number | null;
  currency?: string | null;
}

export interface AdminOrderListResponse {
  items: OrderAdminListItem[];
  total: number;
  page: number;
  pageSize: number;
}
